/**
 * Runtime configuration loader.
 *
 * In deployed environments (S3 + CloudFront), config values are served from
 * /config.json — written by CDK with real CloudFormation-resolved values
 * (API Gateway URL, Cognito domain, etc.).
 *
 * In local dev, falls back to Vite environment variables (import.meta.env).
 */

export interface AppConfig {
  apiUrl: string;
  cognitoDomain: string;
  cognitoClientId: string;
}

let _config: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (_config) return _config;

  // Local dev — use Vite env vars
  if (import.meta.env.DEV) {
    _config = {
      apiUrl: import.meta.env.VITE_API_URL ?? '/api',
      cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? '',
      cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
    };
    return _config;
  }

  // Production / deployed — fetch config.json from S3
  try {
    const res = await fetch('/config.json');
    _config = await res.json();
    return _config!;
  } catch {
    // Fallback to Vite env vars if config.json is missing
    _config = {
      apiUrl: import.meta.env.VITE_API_URL ?? '/api',
      cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? '',
      cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
    };
    return _config;
  }
}

export function getConfig(): AppConfig {
  if (!_config) throw new Error('Config not loaded — call loadConfig() before app render.');
  return _config;
}
